import { z } from "zod/v4";
import { featuresForHirePurposes, type Feature } from "@visioni/a3s-features";

/**
 * The set of report types currently exposed via the client API.
 *
 * These literals are load-bearing data, not a naming choice: they are
 * persisted (`Report` rows keyed on `(tenantId, type, periodStart)`,
 * subscription sort keys `SUB#{reportType}#{siteId}#{assetId}`) and they are
 * the `{type}` segment of every reports URL. Respelling one costs a data
 * migration and a URL change, so this list is append-mostly.
 *
 * A report type is NOT an a3s feature. It was until DEL-744: `FEATURES` held
 * exactly `["wastesense", "roadsense"]` and this enum aliased it. Features have
 * since been renamed to the capability they grant (`waste_detection`) rather
 * than the product that sells it, and two of them (`vehicle_tracker`, `gate`)
 * grant no report at all - so the two vocabularies are separate, and
 * {@link GRANTING_FEATURE} is the join.
 *
 * Adding a report type is a member here plus its granting feature below, plus a
 * `ReportTypeResources` instantiation in {@link ../cdk/realm-reports-stack.ts};
 * everything keyed by `{type}` in the client-API URL space (the list / item /
 * snapshot-url routes) picks the new value up via this enum's wire shape.
 *
 * Why the literal union (not `z.string()`): the `{type}` segment is a hard
 * enumeration on the server side - every plugin that can ever appear here is
 * registered in code, not data. Validating early at the URL boundary means a
 * typo is rejected with a 400 before any DDB query runs, and the decoded type
 * narrows safely into handler dependencies. The wire form is the same string
 * the generator stamps into `ReportEntity.type`, so no codec is needed.
 */
export const REPORT_TYPES = ["wastesense", "roadsense"] as const;

export const ReportType = z.enum(REPORT_TYPES);
export type ReportType = z.infer<typeof ReportType>;

/**
 * The a3s feature each report type is built from.
 *
 * `satisfies Record<ReportType, Feature>` is what keeps the two vocabularies
 * joined: a report type added above fails to compile until it names the feature
 * that grants it, and a feature renamed in `@visioni/a3s-features` fails to
 * compile here. The map is deliberately one-way - several features grant no
 * report, and a feature reached by two hire purposes (`vehicle_tracker`, from
 * both `waste_sense` and `load_sense`) must not drag a report along with it.
 */
const GRANTING_FEATURE = {
  wastesense: "waste_detection",
  roadsense: "line_crossings",
} as const satisfies Record<ReportType, Feature>;

/**
 * The feature a report type is built from - the join to use whenever a report
 * type has to address the asset-features table, which is keyed by feature.
 */
export function grantingFeature(type: ReportType): Feature {
  return GRANTING_FEATURE[type];
}

/**
 * The report types an asset's features entitle it to.
 *
 * Takes open strings rather than `Feature[]` because that is how features reach
 * the client: `features` on the cameras API is `string[]`, and an asset can
 * hold one granted after the reading client build shipped. An unrecognised
 * feature is ignored rather than rejected.
 *
 * Returned in {@link REPORT_TYPES} order, so the result does not depend on the
 * order the features arrived in.
 */
export function reportTypesForFeatures(features: readonly string[]): ReportType[] {
  const held = new Set(features);
  return REPORT_TYPES.filter((type) => held.has(GRANTING_FEATURE[type]));
}

/**
 * The report types a hire's purposes entitle it to.
 *
 * Two hops: the hub purpose -> feature half lives in `@visioni/a3s-features`,
 * and {@link GRANTING_FEATURE} is the feature -> report type half. Most hires
 * reach nothing - a `load_sense` or gate hire grants features, but no report is
 * built from them.
 */
export function reportTypesForHirePurposes(purposes: readonly string[]): ReportType[] {
  return reportTypesForFeatures(featuresForHirePurposes(purposes));
}

/**
 * Path schema for resources templated as `"{type}/..."`.
 *
 * The single-key shape matches the resource template's placeholder set, so the
 * client API URL builder (`expandEndpoint` -> `interpolateResource`) and the
 * Lambda router both see the same `type` parameter without any extra mapping.
 */
export const TypePath = z.object({ type: ReportType });
export type TypePath = z.output<typeof TypePath>;
